import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import {
    ChevronLeft,
    ChevronRight,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2,
    History,
    DollarSign,
    RefreshCw,
    Activity,
} from 'lucide-react';
import SLOImpactBadge from './SLOImpactBadge.jsx';
import SLOImpactPanel from './SLOImpactPanel.jsx';

import { parseUtc } from '../lib/parseUtc';
const SPIKE_SOURCES = new Set([
    'spike_watcher',
    'spike_watcher_oom',
    'local_spike_detector',
]);

const isSpikeDriven = (item) => {
    if (!item) return false;
    if (SPIKE_SOURCES.has(item.source)) return true;
    if (item.action_category === 'temporary_burst') return true;
    if (item?.metadata && SPIKE_SOURCES.has(item.metadata.source)) return true;
    return false;
};

/**
 * OptimizationHistory - Paginated optimization history component
 * Shows all recent actions with pagination controls
 */
export default function OptimizationHistory({ clusterId }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [sloPanelActionId, setSloPanelActionId] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!clusterId) return;
        setLoading(true);
        try {
            const response = await axios.get(
                `${API}/clusters/${clusterId}/optimization-history?page=${page}&limit=${limit}`
            );
            setHistory(response.data.history || []);
            setTotalPages(response.data.total_pages || 1);
            setTotalCount(response.data.total_count || 0);
        } catch (error) {
            console.error('Failed to fetch optimization history:', error);
        } finally {
            setLoading(false);
        }
    }, [clusterId, page, limit]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return parseUtc(isoString).toLocaleString();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'failed':
                return <XCircle className="w-4 h-4 text-red-500" />;
            case 'pending':
                return <Clock className="w-4 h-4 text-yellow-500" />;
            case 'in_progress':
                return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
            default:
                return <AlertCircle className="w-4 h-4 text-gray-500" />;
        }
    };

    const formatCpu = (value) => {
        if (value == null) return '-';
        if (typeof value === 'string') return value; // already formatted like "20m"
        // Raw number in cores — convert to millicores
        const millis = Math.round(value * 1000);
        if (millis >= 1000) return `${(millis / 1000).toFixed(1)}`;
        return `${millis}m`;
    };

    const formatMemory = (value) => {
        if (value == null) return '-';
        if (typeof value === 'string') return value; // already formatted like "337Mi"
        // Raw number in bytes — convert to human-readable
        const bytes = Math.round(value);
        if (bytes >= 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024 * 1024))}Gi`;
        if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}Mi`;
        if (bytes >= 1024) return `${Math.round(bytes / 1024)}Ki`;
        return `${bytes}`;
    };

    const getStatusBadge = (status) => {
        const styles = {
            completed: 'bg-green-100 text-green-800 border-green-200',
            failed: 'bg-red-100 text-red-800 border-red-200',
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
            requires_approval: 'bg-purple-100 text-purple-800 border-purple-200'
        };
        return styles[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    return (
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-lg">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <History className="w-5 h-5 text-indigo-600" />
                        Optimization History
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={fetchHistory}
                            className="text-slate-600 hover:text-slate-900"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Badge variant="outline" className="text-xs">
                            {totalCount} total
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    </div>
                ) : history.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        No optimization history yet
                    </div>
                ) : (
                    <>
                        {/* History Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200">
                                        <th className="text-left py-2 px-2 text-xs font-medium text-slate-500 uppercase">Workload</th>
                                        <th className="text-left py-2 px-2 text-xs font-medium text-slate-500 uppercase">Status</th>
                                        <th className="text-left py-2 px-2 text-xs font-medium text-slate-500 uppercase">Resources</th>
                                        <th className="text-left py-2 px-2 text-xs font-medium text-slate-500 uppercase">SLO Impact</th>
                                        <th className="text-left py-2 px-2 text-xs font-medium text-slate-500 uppercase">Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((item, index) => (
                                        <tr key={item._id || index} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                                            <td className="py-3 px-2">
                                                <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5">
                                                    {item.workload_name}
                                                    {isSpikeDriven(item) && (
                                                        <Badge className="text-[10px] px-1.5 py-0 h-4 bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-500/30 flex items-center gap-0.5">
                                                            <Activity className="w-2.5 h-2.5" />
                                                            Spike
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                    {item.namespace}
                                                </div>
                                            </td>
                                            <td className="py-3 px-2">
                                                <Badge className={`text-xs ${getStatusBadge(item.status)}`}>
                                                    {getStatusIcon(item.status)}
                                                    <span className="ml-1">{item.status}</span>
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-2">
                                                {item.recommended_resources && (
                                                    <div className="text-xs space-y-0.5">
                                                        <div className="text-slate-600">
                                                            CPU: {formatCpu(item.recommended_resources.cpu_request ?? item.recommended_resources.cpu)}
                                                        </div>
                                                        <div className="text-slate-600">
                                                            Mem: {formatMemory(item.recommended_resources.memory_request ?? item.recommended_resources.memory)}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3 px-2">
                                                <div className="flex items-center gap-2">
                                                    <SLOImpactBadge
                                                        sloStatus={item.slo_status}
                                                        sloConfidence={item.slo_confidence}
                                                        sloPostApply={item.slo_post_apply}
                                                        compact
                                                    />
                                                    {item.slo_status && item.slo_status !== 'not_evaluated' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setSloPanelActionId(item.id || item._id)}
                                                            className="text-[11px] text-indigo-600 hover:text-indigo-800 hover:underline"
                                                        >
                                                            View
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3 px-2">
                                                <div className="text-xs text-slate-600">
                                                    {formatTime(item.created_at)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                            <div className="text-sm text-slate-600">
                                Page {page} of {totalPages}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="text-slate-600"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Prev
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                    className="text-slate-600"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </CardContent>
            <SLOImpactPanel
                actionId={sloPanelActionId}
                open={sloPanelActionId != null}
                onOpenChange={(o) => { if (!o) setSloPanelActionId(null); }}
            />
        </Card>
    );
}
